/**
 * 仓库同步 /warehouse/sync 请求体加密（AES-256-GCM，与 server.js 中 WAREHOUSE_SYNC_AES_KEY 一致）。
 * 服务端仅接受密文体；与 HTTPS 叠加；前端内置密钥仍可被逆向，非绝对保密。
 */

/** 32 字节 AES-256 密钥的十六进制（须正好 64 个 [0-9a-f] 字符），须与 server.js 内 WAREHOUSE_SYNC_AES_KEY 相同 */
export const WAREHOUSE_SYNC_AES_KEY_HEX = "7a3c9e2f1b8d4a6c5e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d";

function hexToKeyBytes(hex) {
    const c = String(hex || "").replace(/\s/g, "");
    if (c.length !== 64) {
        return null;
    }
    const u8 = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
        u8[i] = parseInt(c.slice(i * 2, i * 2 + 2), 16);
    }
    return u8;
}

/**
 * 二进制转 base64。禁止对数万长度使用 apply，否则在部分环境会报 Maximum call stack 导致加密失败。
 */
function u8ToB64(u8) {
    const len = u8.length;
    if (len === 0) {
        return "";
    }
    const CH = 0x1fff;
    const parts = [];
    for (let i = 0; i < len; i += CH) {
        const end = Math.min(i + CH, len);
        let s = "";
        for (let j = i; j < end; j++) {
            s += String.fromCharCode(u8[j]);
        }
        parts.push(s);
    }
    return btoa(parts.join(""));
}

/**
 * @param {object} obj 与原先 POST body 相同：platform, phone, uid, userName, items
 * @returns {Promise<{ e: 1, d: string }>} e=1 表示密文，d=base64(iv(12) + 密文+tag)
 */
export async function encryptWarehouseSyncPayloadJson(obj) {
    const keyBytes = hexToKeyBytes(WAREHOUSE_SYNC_AES_KEY_HEX);
    if (!keyBytes) {
        const L = String(WAREHOUSE_SYNC_AES_KEY_HEX || "").replace(/\s/g, "").length;
        throw new Error(`WAREHOUSE_SYNC_AES_KEY_HEX 无效（AES-256 须正好 64 个十六进制字符，当前 ${L}）`);
    }
    if (!globalThis.crypto?.subtle) {
        throw new Error("Web Crypto 不可用（须 HTTPS 或 localhost/127.0.0.1 等安全上下文）");
    }
    const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, ["encrypt"]);
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const plain = new TextEncoder().encode(JSON.stringify(obj));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        key,
        plain
    );
    const out = new Uint8Array(12 + ct.byteLength);
    out.set(iv, 0);
    out.set(new Uint8Array(ct), 12);
    return { e: 1, d: u8ToB64(out) };
}
